import { useMemo, useSyncExternalStore } from "react";
import { startUpload, readDuration, deleteVideo, reportProgress } from "@/lib/videoApi";

// App-level upload manager: uploads live here (not in any form component), so changing steps never stops them.
const items = new Map();
const listeners = new Set();
const ups = {};
let snap = [];
const versions = {};

function emit() {
  snap = [...items.values()].map((x) => ({ ...x }));
  listeners.forEach((l) => l());
}
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
const getSnap = () => snap;
const set = (key, p) => { const it = items.get(key); if (it) { items.set(key, { ...it, ...p }); emit(); } };

export const fileKey = (f) => `${f.name}|${f.size}|${f.lastModified}`;
export const isActive = (x) => x.status === "waiting" || x.status === "uploading";
export const STALL_MS = 30000;

export function useUploads(scopeId) {
  const all = useSyncExternalStore(subscribe, getSnap);
  return useMemo(() => all.filter((x) => x.scopeId === scopeId), [all, scopeId]);
}
export const useScopeVersion = (scopeId) => useSyncExternalStore(subscribe, () => versions[scopeId] || 0);
export const activeUploads = (scopeId) => [...items.values()].filter((x) => x.scopeId === scopeId && isActive(x));

window.addEventListener("beforeunload", (e) => {
  if ([...items.values()].some(isActive)) { e.preventDefault(); e.returnValue = ""; }
});

// Connection back: resume network-failed or stalled uploads from Cloudflare's saved offset (no re-send from 0).
export function resumeUpload(key) {
  const it = items.get(key);
  if (!it || !ups[key]) return false;
  set(key, { status: "uploading", error: null, lastProgressAt: Date.now() });
  ups[key].abort().finally(() => ups[key]?.start());
  return true;
}
window.addEventListener("online", () => {
  [...items.values()].filter((x) => (x.status === "failed" && x.resumable) || (x.status === "uploading" && Date.now() - x.lastProgressAt > STALL_MS)).forEach((x) => resumeUpload(x.key));
});

export async function addUpload(scope, file, { retryId, maxSeconds } = {}) {
  const key = fileKey(file);
  if (items.get(key) && isActive(items.get(key))) return { error: `${file.name} is already uploading.` };
  if (items.get(key)?.resumable && resumeUpload(key)) return {};
  items.set(key, { key, scopeId: scope.id, name: file.name, size: file.size, status: "waiting", progress: 0, error: null, videoId: retryId || null, lastProgressAt: Date.now() });
  emit();
  const d = await readDuration(file);
  if (d && maxSeconds && d > maxSeconds + 1) {
    items.delete(key); emit();
    return { error: `${file.name} is longer than ${Math.round(maxSeconds / 60)} minutes.` };
  }
  let lastSent = 0;
  let lastBytes = -1;
  set(key, { status: "uploading", lastProgressAt: Date.now() });
  ups[key] = startUpload(scope, file, {
    retryId,
    onVideoId: (id) => set(key, { videoId: id }),
    onProgress: (p, bytes) => {
      if (bytes !== lastBytes) { lastBytes = bytes; set(key, { progress: p, lastProgressAt: Date.now(), status: "uploading", error: null }); }
      const id = items.get(key)?.videoId;
      if (id && (p - lastSent >= 10 || p === 100)) { lastSent = p; reportProgress(id, scope.token, p); }
    },
    onDone: () => { delete ups[key]; items.delete(key); versions[scope.id] = (versions[scope.id] || 0) + 1; emit(); },
    onError: (m, resumable) => { set(key, { status: "failed", error: m, resumable }); versions[scope.id] = (versions[scope.id] || 0) + 1; emit(); },
  });
  return {};
}

export async function cancelUpload(key, token) {
  const it = items.get(key);
  if (!it) return;
  await ups[key]?.abort?.(true);
  delete ups[key];
  items.delete(key);
  emit();
  if (it.videoId) await deleteVideo(it.videoId, token).catch(() => {});
  versions[it.scopeId] = (versions[it.scopeId] || 0) + 1;
  emit();
}
